import {useEffect, useRef} from 'react';

/** Мягко дрейфующее пятно света из квадратных точек — фон карточек. Цвет берётся из акцента студии. */
export function DotGlow({seed = 0, pitch = 6}: {seed?: number; pitch?: number}) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let width = 0;
    let height = 0;
    let dpr = 1;
    let raf = 0;
    let visible = true;
    let last = 0;
    let rgb: [number, number, number] = [70, 144, 255];

    const readAccent = () => {
      const hex = getComputedStyle(canvas).getPropertyValue('--tenant-accent').trim();
      const digits = /^#?([0-9a-f]{6})$/i.exec(hex)?.[1];
      if (digits) rgb = [parseInt(digits.slice(0, 2), 16), parseInt(digits.slice(2, 4), 16), parseInt(digits.slice(4, 6), 16)];
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      readAccent();
    };

    const draw = (time: number) => {
      ctx.clearRect(0, 0, width, height);
      const sigma = Math.min(width, height) * 0.24;
      const cx = width * (0.5 + 0.34 * Math.sin(time * 0.00032 + seed));
      const cy = height * (0.5 + 0.36 * Math.sin(time * 0.00025 + seed * 2 + 1));
      const haze = ctx.createRadialGradient(cx, cy, 0, cx, cy, sigma * 2.3);
      haze.addColorStop(0, `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.22)`);
      haze.addColorStop(1, `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0)`);
      ctx.fillStyle = haze;
      ctx.fillRect(0, 0, width, height);
      const cx2 = width * (0.5 + 0.4 * Math.sin(time * 0.0002 + seed + 3));
      const cy2 = height * (0.5 + 0.4 * Math.cos(time * 0.00027 + seed));
      for (let y = pitch / 2; y < height; y += pitch) {
        for (let x = pitch / 2; x < width; x += pitch) {
          const d1 = ((x - cx) ** 2 + (y - cy) ** 2) / (2 * sigma * sigma);
          const d2 = ((x - cx2) ** 2 + (y - cy2) ** 2) / (2 * sigma * sigma * 0.7);
          const v = Math.min(1, Math.exp(-d1) + 0.25 * Math.exp(-d2));
          const size = pitch * (0.12 + 0.46 * v);
          const white = v * v * 0.4;
          const r = Math.round(rgb[0] + (255 - rgb[0]) * white);
          const g = Math.round(rgb[1] + (255 - rgb[1]) * white);
          const b = Math.round(rgb[2] + (255 - rgb[2]) * white);
          ctx.fillStyle = `rgba(${r},${g},${b},${(0.015 + 0.6 * v).toFixed(3)})`;
          ctx.fillRect(x - size / 2, y - size / 2, size, size);
        }
      }
    };

    const loop = (time: number) => {
      raf = requestAnimationFrame(loop);
      if (!visible || time - last < 33) return;
      last = time;
      draw(time);
    };

    resize();
    draw(seed * 1000);
    const resizeObserver = new ResizeObserver(() => {
      resize();
      draw(performance.now());
    });
    resizeObserver.observe(canvas);
    const visibilityObserver = new IntersectionObserver((entries) => {
      visible = entries.some((entry) => entry.isIntersecting);
    });
    visibilityObserver.observe(canvas);
    if (!reduce) raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
    };
  }, [seed, pitch]);

  return <canvas ref={ref} aria-hidden className="dot-glow" />;
}
